import React, { useEffect, useRef, useState } from 'react';
import './CatalogSelect.css';

/**
 * CatalogSelect — selector de catálogo con el diseño del POS v64 (pill + menú).
 * Mantiene TU lógica: catalogs, catalogSelect y selectCatalog (mismo onChange).
 *
 * @param {Array}  catalogs        tu arreglo de catálogos [{catalog_id, name}]
 * @param {string|number} catalogSelect   valor seleccionado actual (id o "0")
 * @param {(catalogOr0)=>void} selectCatalog   tu función (recibe 0 o el objeto catálogo)
 */
export function CatalogSelect({ catalogs = [], catalogSelect, selectCatalog }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  // Etiqueta actual (misma semántica que tu <select>)
  const current = String(catalogSelect) === '0' || catalogSelect == null
    ? null
    : catalogs.find((c) => c.catalog_id.toString() === String(catalogSelect));
  const label = current ? current.name : 'Ver Todo';

  // Mismo comportamiento que tu onChange, pero recibiendo el "value"
  const pick = (value) => {
    if (value === '0') {
      selectCatalog(0);
    } else {
      const selectedCatalog = catalogs.find((catalog) => catalog.catalog_id.toString() === value);
      if (selectedCatalog) selectCatalog(selectedCatalog);
    }
    setOpen(false);
  };

  if (catalogs.length === 0) {
    return (
      <div className="cs-empty" role="alert">
        <i className="uil uil-info-circle"></i> No hay catálogos disponibles
      </div>
    );
  }

  const isOn = (val) => String(catalogSelect ?? '0') === String(val);

  return (
    <div className={`cat-select ${open ? 'open' : ''}`} ref={ref}>
      <button className="pill" type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" />
        </svg>
        <span className="cs-label">{label}</span>
        <svg className="cs-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="6 9 12 15 18 9" /></svg>
      </button>

      <div className="cat-menu" role="listbox">
        {/* Opción "Ver Todo" (value 0) */}
        <button type="button" className={isOn('0') ? 'on' : ''} onClick={() => pick('0')} role="option" aria-selected={isOn('0')}>
          <span>Ver Todo</span>
          <svg className="ck" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><polyline points="20 6 9 17 4 12" /></svg>
        </button>

        {/* Catálogos */}
        {catalogs.map((catalog) => {
          const val = catalog.catalog_id.toString();
          return (
            <button key={catalog.catalog_id} type="button" className={isOn(val) ? 'on' : ''}
              onClick={() => pick(val)} role="option" aria-selected={isOn(val)}>
              <span>{catalog.name}</span>
              <svg className="ck" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><polyline points="20 6 9 17 4 12" /></svg>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default CatalogSelect;